/**
 * SPA bootstrap bridge: silently exchange an Amplify/Cognito session for
 * a `simpl_auth` cookie via `POST {AUTH_HOST}/api/auth/cognito-exchange`.
 *
 * Without this, every currently-signed-in user would face a fresh login
 * prompt the first time they loaded the post-cutover SPA — purely because
 * their browser has Amplify/Cognito material and no `SIMPL_AUTH_JWT_<env>`
 * cookie yet. The bridge converts that latent Cognito session into the
 * new cookie format on first load, so the migration is invisible to
 * existing users.
 *
 * Lives in this repo (not in `simpl_auth`) because Amplify's localStorage
 * is origin-scoped to `platform.simpl.org` / `platform-test.simpl.org`;
 * `auth.simpl.org` has no cross-origin read access to it.
 *
 * Lifecycle:
 *
 *   1. The session probe in `simplAuthSession.ts` returns `unauthed`
 *      (no `SIMPL_AUTH_JWT_<env>` cookie, no Cognito header that the
 *      authorizer accepted).
 *   2. Caller invokes `attemptCognitoExchange()`. If Amplify has no
 *      session, this short-circuits to `"no_session"` — caller falls
 *      through to the existing sign-in path.
 *   3. Amplify refreshes the Cognito ID + access tokens locally via
 *      `Auth.currentSession()`. We POST both to the simpl_auth exchange
 *      endpoint with `credentials: "include"` so the response's
 *      `Set-Cookie` lands on the SPA's cookie jar. The access token
 *      lets simpl_auth seed an MFA-pending session in the 403/setup
 *      branch so `/mfa/setup` can render for the diverted user.
 *   4. On success, we clear Amplify's localStorage via `Auth.signOut()`.
 *      This ensures the transition is one-way: once the browser has a
 *      `simpl_auth` cookie, we no longer rely on (or fallback to) the
 *      old Cognito material.
 *   5. On any failure (refresh failed, exchange rejected, network
 *      error), caller falls through to the existing sign-in path.
 *
 * The bridge is temporary scaffolding aligned with the §6.7 sunset of
 * `/api/auth/cognito-exchange`. Once that endpoint returns 410 Gone,
 * this module and the residual `aws-amplify` dep can be deleted.
 */

import { Auth } from "aws-amplify";

import { getSimplAuthHost } from "./simplAuthSession";

export type CognitoExchangeResult =
  | "success"
  | "no_session"
  | "mfa_enrollment_required"
  | "exchange_failed";

/**
 * Body of the 403 response from `POST /api/auth/cognito-exchange` when the
 * effective MFA policy requires the user to enroll before we mint a session.
 * Mirrors `_bridge_mfa_gate` in `simpl_auth/src/main/app.py` (SPR-113 Tier 2d).
 */
interface MfaEnrollmentRequiredBody {
  status: "mfa_enrollment_required";
  setup_url: string;
  sms_allowed?: boolean;
}

interface CognitoTokenPair {
  idToken: string;
  accessToken: string | null;
}

async function getFreshCognitoTokens(): Promise<CognitoTokenPair | null> {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    if (!idToken) return null;
    // The access token is needed by simpl_auth to call
    // `associate_software_token` on the user's behalf if the bridge
    // diverts to /mfa/setup (SPR-113 Tier 2d). It's not used for the
    // 200/success path (the bridge uses admin APIs there), so this is
    // best-effort: a missing access token still lets the bridge mint a
    // session for users who don't need MFA enrollment.
    let accessToken: string | null = null;
    try {
      accessToken = session.getAccessToken().getJwtToken() || null;
    } catch {
      accessToken = null;
    }
    return { idToken, accessToken };
  } catch {
    // `currentSession()` rejects when there's no Amplify-managed user.
    // That's the dominant case post-cutover for users who have already
    // re-signed-in via simpl_auth.
    return null;
  }
}

/**
 * Attempt the silent Cognito → simpl_auth exchange. See module-level
 * doc for lifecycle. Returns:
 *
 *   - `"success"`                 - cookie minted; caller should re-probe
 *                                   the session (which will now report authed).
 *   - `"no_session"`              - Amplify has no Cognito session to trade;
 *                                   caller should redirect to sign-in.
 *   - `"mfa_enrollment_required"` - the user's effective policy requires
 *                                   MFA but Cognito reports them as not
 *                                   enrolled. Caller has been navigated to
 *                                   the simpl_auth /mfa/setup flow already;
 *                                   no further action required.
 *   - `"exchange_failed"`         - we had a Cognito session but the
 *                                   exchange was rejected (expired /
 *                                   unknown user / network failure);
 *                                   caller should redirect to sign-in.
 *
 * The MFA-enrollment redirect happens inside this function so the SPA's
 * fallback paths (Amplify Authenticator, sign-in redirect) don't briefly
 * flicker visible while the browser navigates away.
 */
export async function attemptCognitoExchange(): Promise<CognitoExchangeResult> {
  const tokens = await getFreshCognitoTokens();
  if (!tokens) return "no_session";

  const exchangeUrl = `${getSimplAuthHost()}/api/auth/cognito-exchange`;
  let response: Response;
  try {
    response = await fetch(exchangeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        cognito_token: tokens.idToken,
        cognito_access_token: tokens.accessToken ?? undefined,
        client: "web"
      })
    });
  } catch {
    return "exchange_failed";
  }

  if (response.ok) {
    // Exchange succeeded. Clear the old Cognito material immediately so
    // the client is fully transitioned to the new cookie-based system.
    // This prevents the "fallback loop" where an expired cookie could
    // silently re-bridge itself using an old token.
    try {
      await Auth.signOut();
    } catch {
      // Best effort; if signOut fails, we still return success because
      // the new cookie is already in the cookie jar.
    }
    return "success";
  }

  if (response.status === 403) {
    const body = await safelyParseMfaBody(response);
    if (body?.status === "mfa_enrollment_required" && body.setup_url) {
      window.location.assign(body.setup_url);
      return "mfa_enrollment_required";
    }
  }

  return "exchange_failed";
}

async function safelyParseMfaBody(
  response: Response
): Promise<MfaEnrollmentRequiredBody | null> {
  try {
    return (await response.json()) as MfaEnrollmentRequiredBody;
  } catch {
    return null;
  }
}
